import NewRelicService from '@Services/NewRelicService';
import { Licenses, loginTypes } from '@Constants/CommonConstants';
import { config, FEATURES } from '@Constants/rx-ui/constants';
import { RememberMeSettings, UserConfig } from '@Constants/rx-user/constants';
import { SessionExpiredMessage } from '@Constants/User';
import { NODE_ENV, PENDO_FEEDBACK_URL, PENDO_URL } from '@Constants/BuildInfo';
import UserInterfaceConfig from '@Services/UserInterfaceConfig';
import RoutingConfig from '@Constants/rx-user/routing-config.provider';
import RequestUrls from '@Constants/RequestUrlsConstants';
import * as BrowserHelper from '@Services/BrowserHelper';
import MessageBroker from '@Services/MessageBroker';
import Deferred from '@Utils/Deferred';

import store from '@Store';
import { currentUserUpdated } from '@Containers/Header/CurrentUserActions';
import { updateUserDetails } from '@Containers/UserDetails/UserDetailsActions';
import get from 'lodash/get';
import hash from 'object-hash';
import cloneDeep from 'lodash/cloneDeep';
import findKey from 'lodash/findKey';
import extend from 'lodash/extend';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';

import { getLoginCustomNotifications } from '@Services/LoginCustomNotifications/LoginCustomNotifications';
import AppStorage from '@Services/AppStorage';
import AccountData from '@Services/AccountData';
import Catalyst from '@Services/Catalyst';
import HttpRequest from '@Services/RequestFactory';

import Login from '@Services/Login';

import { clearTraceId } from '@Services/CorrelationId';
import { encryptUsernameAndPassword } from '@Services/Encrypt';
import PendoService from '@Services/Pendo';
import UsabillaService from '@Services/Usabilla';
import ReencryptionNotify from '@Services/ReencryptionNotify';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { updateDialog } from '@Containers/Dialogs/DialogsActions';
import { typeOfLogoutConfirmModal } from '@Containers/LogoutConfirmModal/LogoutConfirmModalConstants';

import Cookies from 'js-cookie';
import AuthUser from '@Services/Auth/AuthUser';
import AuthCleanUp from '@Services/Auth/AuthCleanUp';
import { updatedUserConstants } from './UpdateUserConstants';

import * as AuthHelper from './AuthHelper';
import * as AuthUtils from './AuthUtils';

const { accessLevels } = RoutingConfig;
const { userRoles } = RoutingConfig; // empty object for a clean client

let commonQueryParams = {
  client_id: UserConfig.clientId,
  client_secret: UserConfig.clientSecret,
};

let isCurrentlyLoggingOutVal = false;

let isGrantAnonymousVal = false;

AppStorage.instance.set('currentUser', AuthUser.currentUser);

export const getAccessLevels = () => cloneDeep(accessLevels);

export const getUserRoles = () => cloneDeep(userRoles);

export const getUser = () => AuthHelper.getUser();

export const getUserName = () =>
  AuthUser.currentUser &&
  AuthUser.currentUser.details &&
  `${AuthUser.currentUser.details.firstName} ${AuthUser.currentUser.details.lastName}`;

export const isAnonymousUser = () => AuthHelper.isAnonymousUser();

export const isGrantAnonymoususer = () => isGrantAnonymousVal;

export const isUpgradedFromAnonymous = () => AuthHelper.isUpgradedFromAnonymous();

export const isRegistrationEnabled = () => get(AuthUser.currentUser, 'permissions', []).indexOf('REGISTRATION_PAGE_ONLY') !== -1;

export const isReaxysAllowed = () => AuthHelper.isReaxysAllowed();

export const getLicense = (mapped = false) => {
  const rawLicense = get(AuthUser.currentUser, 'details.license');
  return mapped ? findKey(Licenses, (value) => value === rawLicense) : rawLicense;
};

export const isRMCLicense = () => AuthHelper.isRMCLicense();

export const isClassicLicense = () => {
  if (AuthUser.currentUser && AuthUser.currentUser.details) {
    return Licenses.Reaxys === AuthUser.currentUser.details.license;
  }
  return false;
};

export const isFeatureEnabled = (feature) => AuthHelper.isFeatureEnabled(feature);

export const getDevOptions = () => AuthHelper.getDevOptions();

export const isShibbolethUser = () => AuthUser.currentUser.details && AuthUser.currentUser.details.authType === 'institution-sso';

export const isAnonShibbolethLogin = () =>
  get(AuthUser.currentUser, 'details.loginDetails.accessType', '').indexOf('ANON_SHIBBOLETH:SHIBBOLETH') !== -1;

const _getGDPRSafeProps = (user) => AuthUtils._getGDPRSafeProps(user);

export const updateUser = (user, force) => {
  // --- Text and Contrast settings BEGIN ---
  const oldTextSize = get(AuthUser.currentUser, 'details.settings.pageStyleTextSize');
  const newTextSize = get(user, 'details.settings.pageStyleTextSize');
  const textColor = get(user, 'details.settings.pageStyleTextColor');
  const contrast = get(user, 'details.settings.pageStyleTextContrast');
  const features = !isEmpty(get(user, 'details.features', null));

  if (newTextSize) {
    UserInterfaceConfig.applyUserFontSizeSettings(oldTextSize, newTextSize);
  }

  if (textColor || contrast) {
    UserInterfaceConfig.applySchema(textColor, contrast);
  }
  // --- Text and Contrast settings END ---

  const oldPageSize = get(AuthUser.currentUser, 'details.settings.pageSize');
  let newPageSize = get(user, 'details.settings.pageSize', false);

  if (newPageSize && oldPageSize !== newPageSize) {
    // for IE default page size value is 15 and max value is 30, so if user has >=30 items in preferences -> set 30
    // otherwise set page size value from user's preferences
    if (BrowserHelper.detectOldIE() && get(AuthUser.currentUser, 'devOptions.resultsPerPageIELimit', true)) {
      newPageSize = newPageSize > 15 ? 30 : newPageSize;
    }
    AppStorage.instance.set('resultsPerPage', newPageSize);
  }
  if (force) {
    AuthUser.currentUser = cloneDeep(user);
  } else {
    extend(AuthUser.currentUser, user);
  }

  if (features) {
    AuthUser.currentUser.devOptions = Object.keys(FEATURES).reduce((acc, key) => {
      acc[key] = isFeatureEnabled(FEATURES[key]);
      return acc;
    }, {});
  }

  AppStorage.instance.set('currentUser', _getGDPRSafeProps(AuthUser.currentUser));

  // Update in React Redux
  store.dispatch(currentUserUpdated(AuthUser.currentUser));
  store.dispatch(updateUserDetails(AuthUser.currentUser.details));

  const disableTracking = get(AuthUser.currentUser, 'details.disableTracking', {});
  if (!isEmpty(disableTracking)) {
    const pendoFeedbackEnabled = isFeatureEnabled('PendoFeedbackTrial');
    if (!pendoFeedbackEnabled) {
      // private usabilla script init
      UsabillaService.init(disableTracking.USABILLA);
    }
    Catalyst.refreshVisitorData(force);

    if (!isUndefined(disableTracking.ADOBE_ANALYTICS)) {
      Catalyst.setNoTracking(disableTracking.ADOBE_ANALYTICS);
    }
    if (!isUndefined(disableTracking.ADOBE_ANALYTICS)) {
      // should be NEW_RELIC
      NewRelicService.setNoTracking(disableTracking.ADOBE_ANALYTICS);
    }

    // Pendo tracking
    const userId = get(AuthUser.currentUser, 'details.loginDetails.userId');
    if (!isUndefined(disableTracking.PENDO) && userId) {
      if (disableTracking.PENDO) {
        // stop tracking and guides after login if feature disabled
        PendoService.disablePendo();
      }

      const url = pendoFeedbackEnabled ? PENDO_FEEDBACK_URL : PENDO_URL;
      PendoService.setNoTracking(disableTracking.PENDO);
      PendoService.link(url)
        .then(() => PendoService.updateVisitor(AuthUser.currentUser))
        .catch(Logger.warn);
    }
  }

  return AuthUser.currentUser;
};

export const authorize = (accessLevel, role) => {
  if (role === undefined) {
    // eslint-disable-next-line no-param-reassign
    role = AuthUser.currentUser.role;
  }
  // eslint-disable-next-line no-bitwise
  return accessLevel && accessLevel.bitMask & role.bitMask;
};

export const isLoggedIn = (user) => {
  if (user === undefined) {
    // eslint-disable-next-line no-param-reassign
    user = AuthUser.currentUser;
  }

  // eslint-disable-next-line no-use-before-define
  return getAccessToken() && (user.role.title === userRoles.user.title || user.role.title === userRoles.admin.title);
};

const getStationId = () => AppStorage.instance.get(config.STATION_ID);

function setStationId(stationId) {
  AppStorage.instance.set(config.STATION_ID, stationId);
}

export const _sendOauthTokenRequest = (payload, options = {}) =>
  MessageBroker.send(RequestUrls.userToken, HttpRequest.generateUrlQuery(payload), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    ...options,
  });

export const getRememberMeToken = () => Cookies.get(RememberMeSettings.token);

const noop = () => {};

export const _accessTokenHandler = ({ response, grantType, resolve, reject }) => {
  try {
    // eslint-disable-next-line no-use-before-define
    setUserNotifications(response);
    if (response && get(response, 'notifications.MISSING_DATABASE_MESSAGE', '')) {
      const errorMessage = response.notifications.MISSING_DATABASE_MESSAGE;
      Catalyst.trackError(
        {
          message: errorMessage,
          component: 'Auth',
        },
        'se-'
      );
      NotifyDialogsService.openModalWindow({
        title: 'Warning',
        text: errorMessage,
        confirmText: 'OK',
        type: 'info',
        backdrop: 'static',
      });
    }

    if (response && get(response, 'encryption_status', 'STOPPED') === 'RUNNING') {
      ReencryptionNotify.openReEncryptionStatusModalWindow();
    }

    const userData = extend(response, {
      role: userRoles.user,
      ...(grantType && { roleType: grantType }),
      devOptions: {
        resultsPerPageIELimit: true,
        enabledReactStructureSynthesisPlanner: false,
        enableDisplaySupplierNumber: true,
        enableSUNIChemicalNamesChanges: true,
        enableReactionExportAsRDFile: true,
        enableStaticQuickSearchExamples: true,
        enableAuthorNameSearchImprovements: true,
        hideRCSFiltersExcludeButton: true,
        hideRCSStructureFilter: true,
        hideRCSSDSFilter: true,
      },
    });

    // eslint-disable-next-line no-use-before-define
    setAccessToken(response.access_token);
    if (response[config.STATION_ID]) {
      setStationId(response.station_id);
    }
    delete response.access_token;

    if (resolve) {
      resolve(updateUser(userData));
    } else {
      updateUser(userData);
    }

    if (!isReaxysAllowed()) {
      AccountData.setVisitorAccessType(AccountData.PRELOGIN_ACCESS_TYPES.GUEST_IP_ENFORCED);

      if (AppStorage.instance.get('shibbolethInfo')) {
        AccountData.setVisitorAccessType(AccountData.PRELOGIN_ACCESS_TYPES.GUEST_SHIBBOLETH_ENFORCED);
      }
    }

    if (response[RememberMeSettings.token]) {
      // eslint-disable-next-line no-use-before-define
      setRememberMeToken(response[RememberMeSettings.expires], response[RememberMeSettings.token]);
    }
  } catch (error) {
    Logger.error(error);
    if (reject) reject(error);
  }
};

export const setAccessToken = (token) => {
  AuthHelper.setAccessToken(token);
};

async function handleLogin(credentials, organization, grantType) {
  if (grantType && grantType.grant_type === 'shibboleth') {
    AppStorage.instance.set('shibbolethInfo', credentials);
  }
  if (grantType && grantType.grant_type === 'anonymous') {
    isGrantAnonymousVal = true;
  } else {
    isGrantAnonymousVal = false;
  }

  // eslint-disable-next-line no-param-reassign
  grantType = grantType || { grant_type: 'password' };
  // eslint-disable-next-line no-param-reassign
  credentials = await encryptUsernameAndPassword(credentials || {});
  // eslint-disable-next-line no-param-reassign
  organization = organization || {};

  const stationId = getStationId() ? { stationId: getStationId() } : {};
  if (grantType.grant_type === 'password') {
    const credentialKey = hash({ userName: credentials.username, password: credentials.password });
    commonQueryParams = { ...commonQueryParams, credentialKey };
  }

  const queryParams = extend({}, grantType, credentials, organization, commonQueryParams, stationId);
  /** needed for case when browser was closed and reopened
   * login should return promise for next method (getUserInfo) to wait the token
   * */
  // eslint-disable-next-line no-use-before-define
  const accessToken = getAccessToken();
  const wait = [{}];

  function isShibLogin() {
    return queryParams.grant_type === 'shibboleth';
  }

  function isTicUrlLogin() {
    return queryParams.grant_type === 'ticketUrl';
  }

  function isNamedLogin() {
    return queryParams.grant_type === 'password';
  }

  function isChoiceScreen() {
    return queryParams.grant_type === 'choice';
  }

  function isValidAnonUser() {
    return accessToken && isAnonymousUser();
  }

  if ((isNamedLogin() || isChoiceScreen() || isShibLogin() || isTicUrlLogin()) && isValidAnonUser()) {
    // if from anon to named
    // case when it's login request after choice screen /#/organisation
    queryParams.accessToken = accessToken;
  } else {
    // TODO Looks suspicious (¬_¬).
    // If there is accessToken - it will process the condition above, if not - why do we need logout?

    // case when named => named or any other
    // case when any => anonymous
    // eslint-disable-next-line no-lonely-if
    if (isChoiceScreen()) {
      noop(); // no need to clean once again if we sent login already
    } else if (isRegistrationEnabled()) {
      // todo check case for registration
      // eslint-disable-next-line no-use-before-define
      wait.push(cleanUp());
    } else {
      // logout needed when we login as named user and token from anonymous is still there
      // eslint-disable-next-line no-use-before-define
      wait.push(logout());
    }
  }

  return new Promise((resolve, reject) => {
    Promise.all(wait).finally(() =>
      _sendOauthTokenRequest(queryParams)
        .then((response) => {
          _accessTokenHandler({
            response,
            grantType,
            resolve,
            reject,
          });
        })
        .catch((error) => {
          reject(error);
          // eslint-disable-next-line no-use-before-define
          setUserNotifications(error);

          function _freshLogin() {
            // eslint-disable-next-line no-use-before-define
            logout()
              .then(() => {
                // eslint-disable-next-line prefer-rest-params
                handleLogin(...arguments)
                  .then(Login.afterAuthRedirect)
                  .catch(Logger.error);
              })
              .catch(Logger.error);
          }

          if (error && error.type === 'CONFIRM') {
            const errorMessage = 'Please note: Reaxys has recognized that your license will change after login.';
            Catalyst.trackError({
              message: errorMessage,
              component: 'Auth',
            });

            NotifyDialogsService.openModalWindow({
              title: `Warning`,
              text: `${errorMessage} This will result in losing all previous searches and results. Do you want to continue?`,
              cancelText: `Cancel`,
              confirmText: `Yes`,
              type: 'info',
              backdrop: 'static',
              onConfirm: () => {
                // eslint-disable-next-line prefer-rest-params
                _freshLogin(...arguments);
              },
              onCancel: () => {
                Login.cancelLogin();
              },
            });
          } else if (error && error.type === 'RELOGIN') {
            /* case when upgrade is not possible
             * e.g. shiboleth or ticrul with anonymous user => anonymous user */
            // eslint-disable-next-line prefer-rest-params
            _freshLogin(...arguments);
          }
        })
    );
  });
}

export const login = async (...args) => handleLogin(...args);

export const recoverSession = (isRemember) => {
  const rememberMeToken = getRememberMeToken();
  if (isRemember && rememberMeToken) {
    return login(null, { remember_me_token: rememberMeToken }, { grant_type: 'remember' });
  }
  return login(null, null, { grant_type: 'anonymous' });
};

export const logout = (isRemoveRemember) => {
  // send disconnect request only if there is an access token with RX2_APPLICATION permission
  // eslint-disable-next-line no-use-before-define
  if (getAccessToken() && isReaxysAllowed()) {
    isCurrentlyLoggingOutVal = true;

    return MessageBroker.send(RequestUrls.userLogout)
      .then(() => Login.afterLogout(isRemoveRemember))
      .finally(() => {
        isCurrentlyLoggingOutVal = false;

        // Clear traceId for trace-context headers
        clearTraceId();
      })
      .catch(Logger.reject);
  }
  return Login.afterLogout(isRemoveRemember);
};

export const cleanUp = () =>
  // clear cache
  AuthCleanUp();

export const getUserInfo = () => {
  updatedUserConstants.updatedUserDefer = Deferred();
  return MessageBroker.send(RequestUrls.userInfo, null, { cache: false });
};

export const getClientInfo = () => AuthUtils.getClientInfo();

export const changeUserProfile = (fromUser, data) => {
  const userData = {
    [fromUser ? 'details' : 'clientInfo']: data,
  };

  updateUser(userData);

  return Promise.resolve(AuthUser.currentUser);
};

export const resetPassword = (email) => MessageBroker.send(RequestUrls.resetPassword, { address: email }, { Authorization: false });

export const resetPasswordWithToken = (token, newPassword) =>
  MessageBroker.send(
    RequestUrls.resetPasswordWithToken,
    {
      token,
      newPassword,
    },
    { Authorization: false }
  );

export const getAccessToken = (what) => AuthHelper.getAccessToken(what);

export const logoutOnExpiredSession = (title, message, logoutOnConfirm = false, forced = false) => {
  const loginType = AppStorage.instance.get('loginType');
  let remember = false;
  let userInstitutionInfo = null;
  let isInstitutionRemembered = false;

  if (loginType === loginTypes.SIMPLE) {
    remember = !!getRememberMeToken();
  } else {
    userInstitutionInfo = AppStorage.instance.get('userInstitutionInfo');
    isInstitutionRemembered = userInstitutionInfo ? !!userInstitutionInfo.rememberChoice : false;
  }

  const content = {
    message: title || SessionExpiredMessage,
    text: message || null,
    confirm: 'OK',
    remember: false,
    hasRemember: remember,
    isInstitutionRemembered,
    institutionRemember: isInstitutionRemembered,
    forced,
  };

  if (!logoutOnConfirm) {
    // eslint-disable-next-line no-use-before-define
    _logout();
  }
  store.dispatch(
    updateDialog({
      id: typeOfLogoutConfirmModal,
      dialogType: typeOfLogoutConfirmModal,
      movable: false,
      backdrop: false,
      closeButton: false,
      className: 'logout-confirm-modal-dialog-wrap',
      // eslint-disable-next-line no-use-before-define
      onConfirm: () => (logoutOnConfirm ? _logout() : Promise.resolve()),
      content,
    })
  );

  function _logout() {
    if (getAccessToken()) {
      return logout(!remember).finally(() => {
        if (!isInstitutionRemembered) {
          AppStorage.instance.remove('userInstitutionInfo');
        }
        AppStorage.instance.remove('loginType');
      });
    }
    return Promise.resolve();
  }
};

export const setRememberMeToken = (days, token) => {
  AuthHelper.setRememberMeToken(days, token);
};

export const checkAEMock = () => {
  // fix to prevent /healthcheck call on prod system
  if (NODE_ENV.indexOf('prod') === 0) {
    return Promise.resolve({});
  }
  return MessageBroker.send(RequestUrls.healthCheck);
};

export const getLastActionTime = () => new Date(AppStorage.instance.get('lastActionTime'));

export const setLastActionTime = (time) => {
  AuthHelper.setLastActionTime(time);
};

export const goToLandingPage = (...args) => AuthHelper.goToLandingPage(...args);

export const getPreregisterInfo = () => MessageBroker.send(RequestUrls.userProfilePreregister);

export const hasInstitutionLogin = () => {
  const permissions = get(AuthUser.currentUser, 'permissions', []);
  return permissions.length ? permissions.indexOf('INSTITUTION_SHIBBOLETH_LOGIN') !== -1 : true;
};

export const isCurrentlyLoggingOut = () => isCurrentlyLoggingOutVal;

export const setUserNotifications = (info) => {
  const notifications = get(info, 'notifications', null);
  AppStorage.instance.set('userNotifications', notifications);
};

export const getUserNotifications = () => ({
  ...AppStorage.instance.get('userNotifications'),
  ...getLoginCustomNotifications(),
});

export const getUpdatedUserPromise = () =>
  updatedUserConstants && updatedUserConstants.updatedUserDefer
    ? updatedUserConstants.updatedUserDefer.promise
    : // eslint-disable-next-line prefer-promise-reject-errors
      Promise.reject("userInfo wasn't requested");
